import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  getBackendServerConfigPath,
  loadBackendServerOptions,
  updateSelectedBackendServer,
  useSelectedBackendServerUrl,
} from '@/lib/backend-servers'

const BUILD_MODE = String(import.meta.env.MODE ?? '').trim().toLowerCase()
const HIDE_BACKEND_SERVER_SELECTOR = BUILD_MODE === 'production' || BUILD_MODE === 'staging'

type BackendServerSelectorProps = {
  className?: string
  disabled?: boolean
}

export function BackendServerSelector({ className, disabled = false }: BackendServerSelectorProps) {
  if (HIDE_BACKEND_SERVER_SELECTOR) {
    return null
  }

  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const configPath = getBackendServerConfigPath()
  const serverOptionsQuery = useQuery({
    queryKey: ['backend-server-options', configPath],
    queryFn: loadBackendServerOptions,
    enabled: Boolean(configPath),
    staleTime: Infinity,
  })

  const serverOptions = useMemo(() => {
    const options = serverOptionsQuery.data ?? []

    if (!selectedBackendServerUrl || options.some((server) => server.url === selectedBackendServerUrl)) {
      return options
    }

    return [{ name: 'Current server', url: selectedBackendServerUrl }, ...options]
  }, [selectedBackendServerUrl, serverOptionsQuery.data])

  const helperMessage = !configPath
    ? 'Server list is disabled because VITE_BACKEND_SERVER_CONFIG_PATH is not set.'
    : serverOptionsQuery.isError
      ? `Unable to load the server list from ${configPath}.`
      : `Server list loaded from ${configPath}.`

  return (
    <div className={className}>
      <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
        <Label htmlFor="backend-server">Backend server</Label>
        <Select
          value={selectedBackendServerUrl || undefined}
          onValueChange={updateSelectedBackendServer}
          disabled={disabled || !configPath || serverOptionsQuery.isLoading || serverOptions.length === 0}
        >
          <SelectTrigger id="backend-server" className="w-full">
            <SelectValue placeholder={serverOptionsQuery.isLoading ? 'Loading servers...' : 'Select a backend server'} />
          </SelectTrigger>
          <SelectContent>
            {serverOptions.map((server) => (
              <SelectItem key={server.url} value={server.url}>
                {server.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{helperMessage}</p>
      </div>
    </div>
  )
}